// Theme Toggle Component
// Provides UI for switching between light/dark/system themes
// Implements PRD Section 5.3 dark mode toggle requirement

import { useTheme } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  variant?: 'button' | 'dropdown';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ThemeToggle({
  variant = 'button',
  size = 'md',
  className = '',
}: ThemeToggleProps) {
  const { theme, setTheme, actualTheme } = useTheme();

  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <select
          value={theme}
          onChange={e =>
            setTheme(e.target.value as 'light' | 'dark' | 'system')
          }
          className={`
            input theme-transition focus-ring
            ${size === 'sm' ? 'text-xs px-2 py-1' : ''}
            ${size === 'md' ? 'text-sm px-3 py-2' : ''}
            ${size === 'lg' ? 'text-base px-4 py-3' : ''}
          `}
          aria-label="Select theme"
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="system">System</option>
        </select>
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        // Cycle through themes: light → dark → system → light
        if (theme === 'light') {
          setTheme('dark');
        } else if (theme === 'dark') {
          setTheme('system');
        } else {
          setTheme('light');
        }
      }}
      className={`
        btn-ghost theme-transition focus-ring
        ${size === 'sm' ? 'btn-sm' : ''}
        ${size === 'lg' ? 'btn-lg' : ''}
        ${className}
      `}
      aria-label={`Switch theme (current: ${theme})`}
      title={`Current theme: ${theme} (${actualTheme})`}
    >
      <span className="flex items-center gap-2">
        {/* Theme icon */}
        <span className="text-lg" role="img" aria-hidden="true">
          {actualTheme === 'dark' ? '🌙' : '☀️'}
        </span>

        {/* Theme label */}
        <span className="capitalize">
          {theme === 'system' ? `System (${actualTheme})` : theme}
        </span>

        {/* Visual indicator for system theme */}
        {theme === 'system' && (
          <span className="text-xs opacity-60" aria-hidden="true">
            Auto
          </span>
        )}
      </span>
    </button>
  );
}

// Simple icon-only theme toggle
export function ThemeToggleIcon({
  size = 'md',
  className = '',
}: Omit<ThemeToggleProps, 'variant'>) {
  const { setTheme, actualTheme } = useTheme();

  const toggleSimple = () => {
    // Simple toggle between light and dark modes for icon-only variant
    setTheme(actualTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <button
      onClick={toggleSimple}
      className={`
        btn-ghost theme-transition focus-ring rounded-full
        ${size === 'sm' ? 'h-8 w-8 text-sm' : ''}
        ${size === 'md' ? 'h-10 w-10 text-base' : ''}
        ${size === 'lg' ? 'h-12 w-12 text-lg' : ''}
        ${className}
      `}
      aria-label={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <span role="img" aria-hidden="true">
        {actualTheme === 'dark' ? '☀️' : '🌙'}
      </span>
    </button>
  );
}

// Theme status indicator (for development/debugging)
export function ThemeStatus() {
  const { theme, actualTheme } = useTheme();

  return (
    <div className="text-xs text-muted-foreground">
      Theme: <span className="font-mono">{theme}</span>
      {theme === 'system' && (
        <>
          {' '}
          → <span className="font-mono">{actualTheme}</span>
        </>
      )}
    </div>
  );
}
